import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { refreshCoachZoomInfo } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { QueryKeys } from '@/lib/queries';
import { UserWithRolesAndProfiles } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';

export interface RefreshZoomInfoProps {
  trigger: ReactNode;
  user: UserWithRolesAndProfiles;
}

export const RefreshZoomInfo = ({ trigger, user }: RefreshZoomInfoProps) => {
  const [open, setOpen] = useState(false);

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: refreshCoachZoomInfo,
  });
  const onSubmit = () => {
    return mutateAsync(user.id)
      .then((result) => {
        if (result?.zoomInviteStatus === 'invited') {
          toast({
            title: 'Zoom invitation sent',
            description:
              'Zoom invitation has been sent to the user. Please accept the invitation to complete the process.',
          });
        } else {
          toast({
            title: 'Zoom information refreshed',
            description: 'Zoom information has been refreshed.',
          });
        }
        queryClient.invalidateQueries({ queryKey: QueryKeys.getUser(user.id) });
        setOpen(false);
      })
      .catch((e) => {
        toast({
          title: 'Failed to refresh Zoom information',
          description: airServerErrorFrom(e)?.userFacingMessage || 'Failed to refresh Zoom information',
        });
      });
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Refresh Zoom information</AlertDialogTitle>
          <AlertDialogDescription>
            Refresh <b>{user.email}</b>'s Zoom information. If there is no Zoom account for this user, a new one will be
            created and an email will be sent to the user to accept the Zoom invitation. If the user has an existing
            Zoom account, its information will be updated to match the latest data from Zoom, including personal meeting
            Id, account license type and account Id.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction>
            <Button
              loading={isPending}
              onClick={(e) => {
                e.preventDefault();
                onSubmit();
              }}
            >
              Submit request
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
